/**
 * trample_agents.ts — WHO bends the grass this frame (physics follow-up A, 2026-10-05; `audit-feel.md` G7). PURE.
 *
 * The trample field (`foliage_trample.ts`, the engine's InfluenceMap sampled inside the wind position node) took the
 * player, and the walking versonas only while tree-wind handles existed (on an open meadow the player alone). Remote
 * players, the grazing herds, the wild creatures and — most visibly — a hovercraft's downwash never touched it: riding
 * still pressed the rider's 0.85 m walking footprint. This gathers every source into ONE bounded list:
 *
 *   · the PLAYER first (the agent budget must never drop your own footprint) — on foot only; riding, the craft's WASH
 *     replaces it; flying, nothing (a ship 200 m up does not part the grass);
 *   · HOVERCRAFT WASH — the local ride and every remote rider: a radial ring push (the map's radial deposit: blades lean
 *     AWAY from the centre) whose radius grows with speed (2.6 → 5 m) and whose strength grows with speed and fades
 *     with the hull's height over its rest ride height (gone ~3.5 m up — a ramp jump stops pressing the meadow);
 *   · other PLAYERS on foot, the walking VERSONAS, the HERD animals (a footprint sized by the body), the WILD CREATURES.
 *
 * COST IS BOUNDED: each kind keeps its NEAREST N inside the field's own region (a deposit outside it is wasted work),
 * and the whole list is capped at TRAMPLE_TOTAL_MAX. A deposit is a Gaussian stamp over the cells inside its radius —
 * at the finest field (0.19 m cells) a 5 m wash is ~2,800 cells; the list costs well under a millisecond.
 */

export interface TrampleAgent { x: number; z: number; radius: number; strength: number; dirX?: number; dirZ?: number }
export interface TramplePoint { x: number; z: number }
export interface TrampleBody extends TramplePoint { lengthM: number }
export interface WashSource { x: number; z: number; speed: number; /** hull height over the ground under it (m) */ gap: number; /** its rest ride height (m) */ hoverM: number }

/** The player's own footprint (unchanged from the 2026-09-14 wiring). */
export const PLAYER_FOOT = { radius: 0.85, strength: 1 } as const;
export const WALKER_FOOT = { radius: 0.8, strength: 0.8 } as const;
export const CREATURE_FOOT = { radius: 1.1, strength: 0.85 } as const;
/** A grazer's footprint is its body: radius = HERD_FOOT_PER_M × length (2–3.2 m beasts → 0.9–1.4 m). */
export const HERD_FOOT_PER_M = 0.44;
export const HERD_FOOT_STRENGTH = 0.7;

/** Hovercraft wash: radius and strength by speed, faded by height over the rest ride height. */
export const WASH_RADIUS_MIN_M = 2.6;
export const WASH_RADIUS_MAX_M = 5;
export const WASH_RADIUS_PER_MPS = 0.06;
export const WASH_STRENGTH_MIN = 0.4;
export const WASH_STRENGTH_MAX = 1.25;
export const WASH_STRENGTH_PER_MPS = 1 / 30;
/** Height over the rest ride height at which the wash is gone (m). */
export const WASH_FADE_M = 3.5;

/** Nearest N per kind, and the whole list. */
export const TRAMPLE_CAP = { wash: 6, remote: 12, versona: 24, herd: 12, creature: 8 } as const;
export const TRAMPLE_TOTAL_MAX = 64;

/** PURE: the wash of one craft, or null when it is too high (or not a number). */
export function washAgent(w: WashSource): TrampleAgent | null {
  if (![w.x, w.z, w.speed, w.gap, w.hoverM].every(Number.isFinite)) return null;
  const over = Math.max(0, w.gap - Math.max(0, w.hoverM));
  const fade = 1 - over / WASH_FADE_M;
  if (fade <= 0) return null;
  const v = Math.max(0, w.speed);
  const radius = Math.min(WASH_RADIUS_MAX_M, WASH_RADIUS_MIN_M + v * WASH_RADIUS_PER_MPS);
  const strength = Math.min(WASH_STRENGTH_MAX, WASH_STRENGTH_MIN + v * WASH_STRENGTH_PER_MPS) * Math.min(1, fade);
  return { x: w.x, z: w.z, radius, strength };
}

export interface GatherInput {
  /** The player (absolute) and the field's centre + side (the region a deposit lands in). */
  px: number; pz: number; cx: number; cz: number; region: number;
  /** On foot = deposit the player's footprint. */
  playerOnFoot: boolean;
  washes?: readonly WashSource[];
  remotes?: readonly TramplePoint[];
  versonas?: readonly TramplePoint[];
  herds?: readonly TrampleBody[];
  creatures?: readonly TramplePoint[];
}

function inRegion(x: number, z: number, r: number, i: GatherInput): boolean {
  const half = i.region / 2 + r;
  return Number.isFinite(x) && Number.isFinite(z) && Math.abs(x - i.cx) <= half && Math.abs(z - i.cz) <= half;
}

/** The nearest `n` of `pts` to the player that land inside the region (stable on ties). */
function nearest<T extends TramplePoint>(pts: readonly T[] | undefined, n: number, r: (p: T) => number, i: GatherInput): T[] {
  if (!pts || n <= 0) return [];
  const keep: Array<{ p: T; d: number }> = [];
  for (const p of pts) {
    if (!inRegion(p.x, p.z, r(p), i)) continue;
    keep.push({ p, d: (p.x - i.px) ** 2 + (p.z - i.pz) ** 2 });
  }
  keep.sort((a, b) => a.d - b.d);
  return keep.slice(0, n).map((k) => k.p);
}

/** PURE: this frame's agents, player first, every kind nearest-first under its cap, the whole list capped. */
export function gatherTrampleAgents(i: GatherInput): TrampleAgent[] {
  const out: TrampleAgent[] = [];
  const push = (a: TrampleAgent | null): void => { if (a && out.length < TRAMPLE_TOTAL_MAX) out.push(a); };
  if (i.playerOnFoot) push({ x: i.px, z: i.pz, radius: PLAYER_FOOT.radius, strength: PLAYER_FOOT.strength });
  // Washes first after the player: the local ride is passed first and is the nearest anyway.
  const wa: TrampleAgent[] = [];
  for (const w of i.washes ?? []) { const a = washAgent(w); if (a && inRegion(a.x, a.z, a.radius, i)) wa.push(a); }
  wa.sort((a, b) => ((a.x - i.px) ** 2 + (a.z - i.pz) ** 2) - ((b.x - i.px) ** 2 + (b.z - i.pz) ** 2));
  for (const a of wa.slice(0, TRAMPLE_CAP.wash)) push(a);
  for (const p of nearest(i.remotes, TRAMPLE_CAP.remote, () => WALKER_FOOT.radius, i)) push({ x: p.x, z: p.z, ...WALKER_FOOT });
  for (const p of nearest(i.versonas, TRAMPLE_CAP.versona, () => WALKER_FOOT.radius, i)) push({ x: p.x, z: p.z, ...WALKER_FOOT });
  for (const p of nearest(i.herds, TRAMPLE_CAP.herd, (b) => b.lengthM * HERD_FOOT_PER_M, i)) push({ x: p.x, z: p.z, radius: Math.max(0.6, p.lengthM * HERD_FOOT_PER_M), strength: HERD_FOOT_STRENGTH });
  for (const p of nearest(i.creatures, TRAMPLE_CAP.creature, () => CREATURE_FOOT.radius, i)) push({ x: p.x, z: p.z, ...CREATURE_FOOT });
  return out;
}

/** PURE: a remote rider's speed from its last two authoritative snapshots (m/s; 0 when unknown). */
export function snapSpeed(snaps: ReadonlyArray<{ t: number; x: number; z: number }> | undefined): number {
  if (!snaps || snaps.length < 2) return 0;
  const a = snaps[snaps.length - 2], b = snaps[snaps.length - 1];
  const dt = (b.t - a.t) / 1000;
  if (!(dt > 0.01)) return 0;
  const v = Math.hypot(b.x - a.x, b.z - a.z) / dt;
  return Number.isFinite(v) ? Math.min(v, 120) : 0;
}
